// AlertsCockpit — Phase D of the Client-Surfaces Programme.
// Desktop alerting cockpit on top of /api/alerts. Filter feed + drawer + actions.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  AlertTriangle, Filter, X, Check, Clock, ArrowUpRight, Loader2, Search,
  ShieldAlert, Wallet, ChefHat, Wrench, UserSquare2, FileWarning, Brain,
  Calendar as CalendarIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITIES = [
  { value: 'critical', label: 'Critical', color: 'bg-danger/10 text-danger border-danger/30' },
  { value: 'high',     label: 'High',     color: 'bg-warn/10 text-warn border-warn/30' },
  { value: 'medium',   label: 'Medium',   color: 'bg-primary-50 text-primary-700 border-primary-200' },
  { value: 'low',      label: 'Low',      color: 'bg-surface-sunken text-ink-muted border-surface-border' },
];

const STATUSES = [
  { value: 'open',          label: 'Open' },
  { value: 'acknowledged',  label: 'Acknowledged' },
  { value: 'in_progress',   label: 'In Progress' },
  { value: 'snoozed',       label: 'Snoozed' },
  { value: 'resolved',      label: 'Resolved' },
];

const CATEGORY_ICON = {
  regulatory: ShieldAlert, financial: Wallet, operational: ChefHat,
  hr: UserSquare2, health_safety: AlertTriangle, vendor: Wrench,
  ai_system: Brain, default: FileWarning,
};

const sevPill = (sev) => SEVERITIES.find(s => s.value === sev) || SEVERITIES[3];

const formatWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.floor(mins / 60)}h ago`;
  return d.toLocaleDateString();
};

const KpiTile = ({ label, value, tone = 'default' }) => (
  <div
    className={`rounded-md border px-4 py-3 ${
      tone === 'danger' ? 'border-danger/30 bg-danger/5'
      : tone === 'warn' ? 'border-warn/30 bg-warn/5'
      : 'border-surface-border bg-surface'
    }`}
    data-testid={`alerts-kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="text-xs uppercase tracking-overline text-ink-muted">{label}</div>
    <div className="font-display text-2xl text-ink mt-1 tabular-nums">{value}</div>
  </div>
);

const AlertsCockpit = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ severity: [], status: ['open', 'acknowledged'], category: [] });
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null); // currently-open alert in drawer
  const [busy, setBusy] = useState(false);
  const [orgId, setOrgId] = useState(null);

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // Resolve active org id once (same pattern as the Navbar): GET /api/platform/context.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) {
          setOrgId(id);
          try { localStorage.setItem('current_organization_id', id); } catch { /* ignore */ }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
  }), [token, orgId]);

  const reload = useCallback(async () => {
    if (!orgId) { setLoading(false); return; } // wait for orgId resolution
    setLoading(true);
    try {
      const params = new URLSearchParams();
      filters.severity.forEach(s => params.append('severity', s));
      filters.status.forEach(s => params.append('status', s));
      filters.category.forEach(c => params.append('category', c));
      params.set('limit', '100');

      const [aRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/alerts?${params.toString()}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/alerts/stats`, { headers: authHeaders }),
      ]);
      setAlerts(aRes.ok ? await aRes.json() : []);
      setStats(sRes.ok ? await sRes.json() : null);
    } catch {
      setAlerts([]); setStats(null);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filters]);

  useEffect(() => { reload(); }, [reload]);

  const toggle = (group, val) => setFilters(f => ({
    ...f,
    [group]: f[group].includes(val) ? f[group].filter(v => v !== val) : [...f[group], val],
  }));

  const filteredAlerts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return alerts;
    return alerts.filter(a =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.description || '').toLowerCase().includes(q)
    );
  }, [alerts, search]);

  const doAction = async (alertId, action, body) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/alerts/${alertId}/${action}`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify(body || {})
      });
      if (res.ok) {
        const updated = await res.json();
        setActive(updated);
        await reload();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Failed: ${err.detail || res.statusText}`);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="alerts-cockpit-page">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-overline text-ink-muted">Stay Compliant</div>
            <h1 className="font-display text-3xl text-ink mt-1">Alerts &amp; Escalation</h1>
            <p className="text-ink-muted text-sm mt-1">Live feed of open, acknowledged and overdue items across the org.</p>
          </div>
          <button
            onClick={reload}
            disabled={loading}
            className="px-3 py-2 text-sm border border-surface-border rounded-md hover:bg-surface motion-organic"
            data-testid="alerts-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}
            Refresh
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="alerts-kpi-strip">
          <KpiTile label="Open" value={stats?.total_open ?? '—'} tone="danger" />
          <KpiTile label="Overdue" value={stats?.overdue ?? '—'} tone="warn" />
          <KpiTile label="Critical" value={stats?.by_severity?.critical ?? '—'} tone="danger" />
          <KpiTile label="Resolved (all-time)" value={stats?.by_status?.resolved ?? '—'} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Filters rail */}
          <aside className="lg:col-span-3 bg-surface border border-surface-border rounded-lg p-4 h-fit" data-testid="alerts-filter-rail">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-ink-muted" />
              <h3 className="font-display text-sm">Filters</h3>
            </div>

            <div className="space-y-4">
              <FilterGroup
                label="Severity"
                options={SEVERITIES}
                selected={filters.severity}
                onToggle={(v) => toggle('severity', v)}
                testidPrefix="alerts-filter-severity"
              />
              <FilterGroup
                label="Status"
                options={STATUSES}
                selected={filters.status}
                onToggle={(v) => toggle('status', v)}
                testidPrefix="alerts-filter-status"
              />
            </div>
          </aside>

          {/* Feed */}
          <section className="lg:col-span-9">
            <div className="bg-surface border border-surface-border rounded-lg overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-surface-border">
                <Search className="w-4 h-4 text-ink-muted" />
                <input
                  type="text"
                  placeholder="Search title or description…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-sm"
                  data-testid="alerts-search-input"
                />
                <span className="text-xs text-ink-muted tabular-nums" data-testid="alerts-result-count">
                  {filteredAlerts.length} shown
                </span>
              </div>

              {loading ? (
                <div className="p-10 text-center text-ink-muted">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                </div>
              ) : filteredAlerts.length === 0 ? (
                <div className="p-12 text-center" data-testid="alerts-empty-state">
                  <ShieldAlert className="w-10 h-10 mx-auto text-ink-subtle mb-3" />
                  <p className="font-display text-lg text-ink">All clear.</p>
                  <p className="text-sm text-ink-muted mt-1">No alerts match the current filters.</p>
                </div>
              ) : (
                <ul>
                  {filteredAlerts.map(a => {
                    const sev = sevPill(a.severity);
                    const Icon = CATEGORY_ICON[a.category] || CATEGORY_ICON.default;
                    return (
                      <li
                        key={a.id}
                        onClick={() => setActive(a)}
                        className="px-4 py-3 border-b border-surface-border last:border-0 hover:bg-surface-sunken cursor-pointer motion-organic"
                        data-testid={`alerts-row-${a.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs border ${sev.color}`} data-testid={`alerts-row-${a.id}-severity`}>
                            {sev.label}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5 text-ink-muted" />
                              <p className="font-medium text-sm text-ink truncate">{a.title}</p>
                            </div>
                            {a.description ? (
                              <p className="text-xs text-ink-muted mt-0.5 line-clamp-2">{a.description}</p>
                            ) : null}
                          </div>
                          <div className="text-xs text-ink-muted tabular-nums whitespace-nowrap" title={a.created_at}>
                            <Clock className="w-3 h-3 inline mr-1" />{formatWhen(a.created_at)}
                          </div>
                          <span className="text-xs uppercase tracking-overline text-ink-subtle" data-testid={`alerts-row-${a.id}-status`}>
                            {a.status}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Drawer */}
      {active ? (
        <div className="fixed inset-0 z-50 flex" data-testid="alert-drawer">
          <div className="flex-1 bg-ink/40" onClick={() => setActive(null)} />
          <aside className="w-full max-w-xl h-full overflow-y-auto bg-surface border-l border-surface-border shadow-lift">
            <div className="sticky top-0 bg-surface border-b border-surface-border px-5 py-4 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-overline text-ink-muted">Alert</div>
                <h2 className="font-display text-lg text-ink mt-1">{active.title}</h2>
              </div>
              <button onClick={() => setActive(null)} className="p-2 rounded-md hover:bg-surface-sunken" data-testid="alert-drawer-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-md border ${sevPill(active.severity).color}`}>{active.severity}</span>
                <span className="text-xs px-2 py-0.5 rounded-md border border-surface-border text-ink-muted">{active.status}</span>
                <span className="text-xs px-2 py-0.5 rounded-md border border-surface-border text-ink-muted">{active.category}</span>
                {typeof active.escalation_level !== 'undefined' && active.escalation_level !== null ? (
                  <span className="text-xs px-2 py-0.5 rounded-md border border-warn/30 bg-warn/5 text-warn">L{active.escalation_level}</span>
                ) : null}
              </div>

              {active.description ? <p className="text-sm text-ink">{active.description}</p> : null}

              {active.due_date ? (
                <div className="flex items-center gap-2 text-sm text-ink-muted">
                  <CalendarIcon className="w-4 h-4" /> Due {new Date(active.due_date).toLocaleString()}
                </div>
              ) : null}

              <div className="grid grid-cols-3 gap-2 pt-2">
                <button
                  disabled={busy || active.status === 'acknowledged' || active.status === 'resolved'}
                  onClick={() => doAction(active.id, 'acknowledge', { notes: 'Acked via cockpit' })}
                  className="px-3 py-2 text-sm rounded-md bg-primary-500 text-surface hover:bg-primary-600 disabled:opacity-40 motion-organic"
                  data-testid="alert-action-ack"
                >
                  <Check className="w-4 h-4 inline mr-1" /> Ack
                </button>
                <button
                  disabled={busy || active.status === 'snoozed' || active.status === 'resolved'}
                  onClick={() => {
                    const until = new Date(Date.now() + 60 * 60 * 1000).toISOString();
                    doAction(active.id, 'snooze', { snooze_until: until, reason: '1 hour snooze' });
                  }}
                  className="px-3 py-2 text-sm rounded-md border border-surface-border hover:bg-surface-sunken disabled:opacity-40 motion-organic"
                  data-testid="alert-action-snooze"
                >
                  <Clock className="w-4 h-4 inline mr-1" /> 1h
                </button>
                <button
                  disabled={busy || active.status === 'resolved'}
                  onClick={() => doAction(active.id, 'escalate', { reason: 'Manual escalate' })}
                  className="px-3 py-2 text-sm rounded-md border border-warn/40 text-warn hover:bg-warn/5 disabled:opacity-40 motion-organic"
                  data-testid="alert-action-escalate"
                >
                  <ArrowUpRight className="w-4 h-4 inline mr-1" /> Escalate
                </button>
              </div>
              <button
                disabled={busy || active.status === 'resolved'}
                onClick={() => doAction(active.id, 'resolve', {
                  action_taken: 'Resolved via cockpit',
                  root_cause: '',
                  preventive_measures: '',
                })}
                className="w-full px-3 py-2 text-sm rounded-md bg-ok text-surface hover:opacity-90 disabled:opacity-40 motion-organic"
                data-testid="alert-action-resolve"
              >
                Mark Resolved
              </button>

              {/* Audit trail */}
              {Array.isArray(active.audit_trail) && active.audit_trail.length > 0 ? (
                <div className="pt-4">
                  <div className="text-xs uppercase tracking-overline text-ink-muted mb-2">Audit Trail</div>
                  <ol className="space-y-2">
                    {active.audit_trail.map((e, i) => (
                      <li key={i} className="text-xs border-l-2 border-surface-border pl-3" data-testid={`audit-entry-${i}`}>
                        <div className="font-medium text-ink">{e.action}</div>
                        <div className="text-ink-muted">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
};

const FilterGroup = ({ label, options, selected, onToggle, testidPrefix }) => (
  <div>
    <div className="text-xs uppercase tracking-overline text-ink-muted mb-2">{label}</div>
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const on = selected.includes(o.value);
        return (
          <button
            key={o.value}
            onClick={() => onToggle(o.value)}
            className={`text-xs px-2.5 py-1 rounded-md border motion-organic ${
              on ? 'bg-primary-500 text-surface border-primary-500' : 'border-surface-border text-ink-muted hover:bg-surface-sunken'
            }`}
            data-testid={`${testidPrefix}-${o.value}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

export default AlertsCockpit;
